import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEngine } from '../api/context';
import { useTheme } from '../design/theme';
import { useHotkeys } from '../layout/shortcuts';
import type { NavSection } from '../module';

// Chrome shared by the Workspace and Desktop skins: the command-palette / shortcut-sheet
// open state and the common hotkeys (⌘K, ?, ⌘⇧L theme, and the "g then x" go-to chords built
// from the nav). The Classic skin keeps its own copy verbatim. Keeping this in one place is
// the DRY rule (CLAUDE.md §7) — a second copy of the hotkey table would be a review blocker.

export function useShellChrome(sections: NavSection[]) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { modules } = useEngine();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const goHotkeys = useMemo(
    () => sections.flatMap((s) => s.items.filter((i) => i.key).map((i) => ({ keys: `g ${i.key}`, run: () => navigate(i.to), label: `Go to ${i.label}`, group: 'Navigate' }))),
    [sections, navigate],
  );

  // Module commands with a global shortcut (e.g. "log time" → mod+shift+l).
  const commandHotkeys = useMemo(
    () =>
      modules
        .flatMap((m) => m.commands ?? [])
        .filter((c) => c.shortcut)
        .map((c) => ({ keys: c.shortcut!, run: () => c.run({ navigate, create: (r) => navigate(`/${r}?new=1`), close: () => undefined }), label: c.label, group: 'Commands', global: true })),
    [modules, navigate],
  );

  useHotkeys([
    { keys: 'mod+k', run: () => setPaletteOpen((v) => !v), label: 'Command palette', group: 'General', global: true },
    { keys: '?', run: () => setSheetOpen(true), label: 'Keyboard shortcuts', group: 'General' },
    { keys: 'mod+shift+l', run: () => theme.toggle(), label: 'Toggle theme', group: 'General', global: true },
    { keys: 'j', run: () => undefined, label: 'Next row', group: 'Lists' },
    { keys: 'k', run: () => undefined, label: 'Previous row', group: 'Lists' },
    { keys: '/', run: () => undefined, label: 'Search the list', group: 'Lists' },
    { keys: 'n', run: () => undefined, label: 'New record', group: 'Lists' },
    { keys: 'e', run: () => undefined, label: 'Edit (record / cell)', group: 'Lists' },
    { keys: 'x', run: () => undefined, label: 'Select row', group: 'Lists' },
    ...goHotkeys,
    ...commandHotkeys,
  ]);

  return { paletteOpen, setPaletteOpen, sheetOpen, setSheetOpen };
}
